import React, {
  type CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useDispatch } from "react-redux";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { cx, styled } from "simplydo/react/styled";
import { mobileOnly } from "simplydo/react/theme";
import {
  Button,
  Icon,
  Message,
  Popup,
  Progress,
  Table,
} from "simplydo/react/ui";

import ReduxActions from "actions";
import api from "api";
import { useAppSelector } from "store";

import { LargeCheckbox } from "components/lib/UI";

import ActionDropdown, { type ActionDropdownProps } from "./ActionDropdown";
import DataRow from "./DataRow";
import TableSettings from "./TableSettings";
import type {
  Column,
  ColumnOption,
  ColumnSetting,
  TableRowItem,
} from "./types";

const actionKey = "__actions";
const minCellWidth = 75;

export type Columns<ItemType extends TableRowItem> = ColumnOption<ItemType>[];

export type ConfigurableTableProps<
  ItemType extends TableRowItem,
  T extends keyof ItemType = keyof ItemType,
> = {
  keyExtractor?: (item: ItemType) => ItemType[T];
  tableKey: string;
  data: ItemType[];
  columns: Columns<ItemType>;
  onRowClick?: (
    item: ItemType,
    event: React.MouseEvent<HTMLTableRowElement>,
  ) => void;
  getRowStyle?: (item: ItemType) => CSSProperties;
  getCellStyle?: (
    column: Column<ItemType>,
    item: ItemType,
    index: number,
  ) => CSSProperties;
  onSelect?: (item: ItemType | null, checked: boolean) => void;
  preventSelectAll?: boolean;
  selectedKeys?: ItemType[T][];
  actions?: ActionDropdownProps<ItemType>["actions"];
  sort?: { key: keyof ItemType; direction: "asc" | "desc" };
  onSort?: (key: keyof ItemType, direction: "asc" | "desc") => void;
  stackable?: boolean;
  wrapperStyle?: CSSProperties;
  containerStyle?: CSSProperties;
  tableStyle?: CSSProperties;
  basic?: boolean;
  headerStyle?: CSSProperties;
  loading?: boolean;
  progress?: number;
};

const TableWrapper = styled.div`
  margin-top: 0;
  align-self: stretch;
  margin-bottom: 10px;
  position: relative;
  max-width: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;

  ${mobileOnly} {
    margin-top: 10px;
  }
`;

type ScrollState = { scrollable: boolean; atStart: boolean; atEnd: boolean };

const StickyScrollButtons = styled.div`
  position: absolute;
  top: 30px;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;

  > div {
    position: sticky;
    display: flex;

    top: 45%;

    justify-content: space-between;
    align-items: center;
    z-index: 4;
  }
`;

const ScrollButton = styled.div<{ $visible?: boolean }>`
  cursor: pointer;
  display: flex;
  width: 50px;
  height: 50px;

  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};

  top: -25px;

  justify-content: center;
  align-items: center;
  position: absolute;
  z-index: 4;
  background: white;
  border-radius: 50%;
  box-shadow: 1px 3px 10px 0 rgba(0, 0, 0, 0.3);
  transition:
    box-shadow 0.15s ease-in-out,
    opacity 0.15s ease-in-out;
  will-change: box-shadow opacity;

  &:hover {
    box-shadow: 1px 3px 10px 0 rgba(0, 0, 0, 0.15);
  }

  i {
    margin: 0;
    margin-top: -4px;
  }
`;

const OverflowContainer = styled.div`
  overflow: auto;
  overscroll-behavior-x: none;

  border-radius: 4px;
  max-width: 100%;
  border: 1px solid rgba(34, 36, 38, 0.15);

  scrollbar-width: thin;
  scrollbar-color: #ccc transparent;

  &:hover {
    scrollbar-color: #aaa transparent;
  }

  .ui.table {
    border-radius: 4px;

    tr:last-child {
      td:first-child {
        border-bottom-left-radius: 4px;
      }
      td:last-child {
        border-bottom-right-radius: 4px;
      }
    }
  }

  table.is-scrollable {
    &::after {
      z-index: 2;
      content: "";
      position: absolute;
      height: 100%;
      left: 0;
      right: 0;
      top: 0;
      border-radius: 4px 0 0 4px;
      pointer-events: none;
      box-shadow:
        inset -16px 0 16px -8px rgba(0, 0, 0, 15%),
        inset 16px 0 16px -8px rgba(0, 0, 0, 15%);
      transition: box-shadow 0.15s ease-in-out;
      will-change: box-shadow;
    }

    &.has-actions::after {
      right: 85px;
    }

    &.at-start:not(.at-end)::after {
      box-shadow:
        inset -16px 0 16px -8px rgba(0, 0, 0, 15%),
        inset 16px 0 16px -8px rgba(0, 0, 0, 0%);
    }

    &.at-end::after {
      box-shadow:
        inset -16px 0 16px -8px rgba(0, 0, 0, 0%),
        inset 16px 0 16px -8px rgba(0, 0, 0, 15%);
    }
  }
`;

const TableProgress = styled(Progress)<{ $visible?: boolean }>`
  position: absolute !important;
  z-index: 500;
  top: 2px;
  left: 2px;
  right: 2px;

  height: 4px;
  margin: 0 !important;
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")} !important;

  .bar {
    height: 100% !important;
    border-radius: 3px !important;
    background-color: #2185d0 !important;
  }
`;

const SettingsIcon = styled.div`
  z-index: 3;
  position: absolute;
  right: 2px;
  top: 2px;
  cursor: pointer;
  padding: 0 2px;
  border-radius: 4px;
  color: #767676;

  :hover {
    background: rgba(0, 0, 0, 0.15);
    box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.15);
    border-radius: 2px;
  }
`;

const SizedHeaderCell = styled(Table.HeaderCell)<{ $preferredWidth?: number }>`
  min-width: ${({ $preferredWidth }) =>
    $preferredWidth
      ? `calc(${$preferredWidth}px + 1.2em)`
      : `${minCellWidth}px`};
  width: ${({ $preferredWidth }) =>
    $preferredWidth ? `calc(${$preferredWidth}px + 1.2em)` : "auto"};

  &&&&.is-sortable:hover {
    cursor: pointer;
    background: #e9e9e9;
  }
`;

const ConfigurableTable = <ItemType extends TableRowItem>({
  tableKey,
  data,
  columns,
  onRowClick,
  sort,
  onSort,
  onSelect,
  preventSelectAll,
  actions,
  selectedKeys = [],
  keyExtractor = (item: ItemType) => item._id as ItemType[keyof ItemType],
  wrapperStyle,
  containerStyle,
  tableStyle,
  basic,
  headerStyle,
  stackable,
  getRowStyle,
  getCellStyle,
  loading,
  progress,
}: ConfigurableTableProps<ItemType>) => {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [scrollState, setScrollState] = useState<ScrollState>({
    scrollable: false,
    atStart: true,
    atEnd: false,
  });

  const dispatch = useDispatch();
  const user = useAppSelector((state) => state.user);
  const savedColumns = useMemo(() => {
    if (!user) {
      return [];
    }
    const { tablePreferences = {} } = user;
    const { columns: userColumns = [] } = tablePreferences?.[tableKey] ?? {};
    return userColumns ?? [];
  }, [user, tableKey]);

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      const effectElement = containerRef.current;
      const trackScroll = (e: Event) => {
        const target = e.target as HTMLElement;
        const { scrollLeft, clientWidth } = target;
        const childNode = target.childNodes[0] as HTMLElement | undefined;
        const innerWidth = childNode?.clientWidth ?? 0;

        const containerWidth = effectElement.clientWidth;
        const tableElement: HTMLTableElement = effectElement
          .childNodes[0] as HTMLTableElement;
        const tableWidth = tableElement.clientWidth;
        const scrollable = containerWidth < tableWidth;
        setScrollState((prev) => ({ ...prev, scrollable }));

        if (scrollLeft === 0) {
          setScrollState((prev) => ({ ...prev, atStart: true }));
        } else {
          setScrollState((prev) => ({ ...prev, atStart: false }));
        }

        if (innerWidth - clientWidth <= scrollLeft + 2) {
          setScrollState((prev) => ({ ...prev, atEnd: true }));
        } else {
          setScrollState((prev) => ({ ...prev, atEnd: false }));
        }
      };

      const containerWidth = effectElement.clientWidth;
      const tableElement: HTMLTableElement = effectElement
        .childNodes[0] as HTMLTableElement;
      const tableWidth = tableElement.clientWidth;
      const scrollable = containerWidth < tableWidth;
      setScrollState((prev) => ({ ...prev, scrollable }));

      effectElement.addEventListener("scroll", trackScroll);

      return () => {
        if (effectElement) {
          effectElement.removeEventListener("scroll", trackScroll);
        }
      };
    }
  }, [loading]);

  const columnSettings = columns
    .filter((col) => !!col && (!("isVisible" in col) || !!col.isVisible))
    .map((column, i) => {
      const savedEntry = savedColumns.find((col) => col.key === column.key);
      return {
        ...column,
        name: column.name ?? column.key,
        enabled: savedEntry?.enabled ?? column.enabled ?? true,
        order: savedEntry?.order ?? i,
      };
    })
    .sort((a, b) => a.order - b.order);
  let enabledColumns = columnSettings.filter((col) => col.enabled);

  const hasActions = !!actions;
  if (hasActions) {
    enabledColumns = [
      ...enabledColumns,
      {
        key: actionKey,
        name: "Actions dropdown",
        width: 75,
        center: true,
        enabled: true,
        order: enabledColumns.length,
      },
    ];
  }

  const selectable = !!onSelect;
  const selectedItems = data.filter((item) =>
    selectedKeys.includes(keyExtractor(item)),
  );

  const updateColumns = (newColumns: Column<ItemType>[] | null) => {
    // Filter props to only save the ones we actually need in the db
    const saveableColumns: ColumnSetting[] | null =
      newColumns?.map((col, index) => ({
        key: col.key,
        enabled: col.enabled ?? true,
        order: col.order ?? index,
      })) ?? null;
    const prevSettings = { ...columnSettings };

    // Apply immediately locally, but revert to previous if the api call fails
    dispatch(
      ReduxActions.user.updateTablePreferences(tableKey, {
        columns: saveableColumns ?? undefined,
      } as Parameters<typeof ReduxActions.user.updateTablePreferences>[1]),
    );
    api.users.updateTablePreferences(
      user?._id ?? "",
      tableKey,
      { columns: saveableColumns ?? undefined },
      () => {},
      () => {
        dispatch(
          ReduxActions.user.updateTablePreferences(tableKey, {
            columns: prevSettings,
          }),
        );
      },
    );
  };

  if (enabledColumns.length === 0) {
    const text =
      data.length > 0
        ? `There are ${data.length} results but all columns are disabled so there is no data to show.`
        : "There are no results and all columns are disabled so there is no data to show.";

    return (
      <>
        <TableSettings
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          updateColumns={updateColumns}
          columns={columnSettings}
        />
        <Message icon warning>
          <Icon name="warning sign" />
          <Message.Content>
            <Message.Header>No columns enabled</Message.Header>
            <p>{text}</p>
            <Button onClick={() => setSettingsOpen(true)}>
              Edit table settings
            </Button>
          </Message.Content>
        </Message>
      </>
    );
  }

  return (
    <TableWrapper style={wrapperStyle}>
      <TableSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        updateColumns={updateColumns}
        columns={columnSettings}
      />
      <TableProgress
        $visible={!!progress || loading}
        indicating
        percent={progress ? progress : loading ? 100 : 0}
      />
      <Popup
        trigger={
          <SettingsIcon onClick={() => setSettingsOpen(true)}>
            <Icon size="small" name="cogs" color="grey" />
          </SettingsIcon>
        }
        offset={[8, 0]}
        content="Edit layout and change settings for this table."
      />
      {scrollState.scrollable ? (
        <StickyScrollButtons>
          <div>
            <ScrollButton
              tabIndex={!scrollState.atStart ? 0 : -1}
              aria-label="Scroll table to the left"
              aria-hidden={!scrollState.atStart}
              $visible={!scrollState.atStart}
              style={{ left: 15 }}
              onClick={() => {
                containerRef.current?.scrollTo({
                  left: containerRef.current?.scrollLeft - 350,
                  behavior: "smooth",
                });
              }}
              onKeyDown={handleClickForKeyboard(() => {
                containerRef.current?.scrollTo({
                  left: containerRef.current?.scrollLeft - 350,
                  behavior: "smooth",
                });
              })}
            >
              <Icon name="arrow left" />
            </ScrollButton>
            <ScrollButton
              tabIndex={!scrollState.atEnd ? 0 : -1}
              aria-label="Scroll table to the right"
              aria-hidden={!scrollState.atEnd}
              $visible={!scrollState.atEnd}
              style={{ right: hasActions ? 100 : 15 }}
              onClick={() => {
                containerRef.current?.scrollTo({
                  left: containerRef.current?.scrollLeft + 350,
                  behavior: "smooth",
                });
              }}
              onKeyDown={handleClickForKeyboard(() => {
                containerRef.current?.scrollTo({
                  left: containerRef.current?.scrollLeft + 350,
                  behavior: "smooth",
                });
              })}
            >
              <Icon name="arrow right" />
            </ScrollButton>
          </div>
        </StickyScrollButtons>
      ) : null}
      <OverflowContainer ref={containerRef} style={containerStyle}>
        <Table
          className={cx(
            scrollState.scrollable && "is-scrollable",
            scrollState.atStart && "at-start",
            scrollState.atEnd && "at-end",
            hasActions && "has-actions",
          )}
          compact="very"
          selectable={!!onRowClick}
          style={{ ...(tableStyle || {}), border: "none" }}
          stackable={stackable}
          unstackable={!stackable}
          basic={basic}
        >
          <Table.Header style={headerStyle}>
            <Table.Row>
              {selectable && !preventSelectAll ? (
                <SizedHeaderCell $preferredWidth={40}>
                  <div
                    style={{
                      display: "flex",
                      flex: 1,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <LargeCheckbox
                      checked={selectedKeys.length > 0}
                      onChange={(e, checked) => {
                        e.preventDefault();
                        onSelect?.(null, checked);
                      }}
                    />
                  </div>
                </SizedHeaderCell>
              ) : selectable ? (
                <SizedHeaderCell $preferredWidth={40} />
              ) : null}
              {enabledColumns.map((col) => (
                <SizedHeaderCell
                  key={col.key}
                  $preferredWidth={col.width}
                  className={cx(col.sortable && onSort && "is-sortable")}
                  //Calls the onSort function, with the key
                  onClick={() =>
                    col.sortable &&
                    onSort &&
                    onSort(
                      col.key,
                      sort?.key !== col.key
                        ? "asc"
                        : sort.direction === "asc"
                          ? "desc"
                          : "asc",
                    )
                  }
                  collapsing={col.collapsing}
                  style={
                    col.key === actionKey
                      ? {
                          position: "sticky",
                          right: -1,
                          zIndex: 1,
                          textAlign: "center",
                          background: "#f9fafb",
                          boxShadow: "-1px 0 1px #ddd",
                          width: 85,
                          minWidth: 85,
                        }
                      : {}
                  }
                >
                  {col.key === actionKey ? (
                    selectable && (
                      <ActionDropdown
                        actions={actions}
                        items={selectedItems}
                        type="multi"
                        user={user}
                      />
                    )
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "nowrap",
                        alignItems: "center",
                        justifyContent: "space-between",
                        lineHeight: col?.compactTitle ? "1" : null,
                      }}
                    >
                      {col.name}
                      {col.sortable && sort && sort.key === col.key ? (
                        <Icon
                          name={`chevron circle ${sort.direction === "asc" ? "up" : "down"}`}
                          style={{ marginTop: -9 }}
                        />
                      ) : null}
                    </div>
                  )}
                </SizedHeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data.map((item, index) => (
              <DataRow
                index={index}
                key={String(keyExtractor(item))}
                selected={selectedKeys.includes(keyExtractor(item))}
                selectable={selectable}
                item={item}
                enabledColumns={enabledColumns}
                onSelect={
                  onSelect
                    ? (item, checked) => onSelect(item, checked)
                    : undefined
                }
                onRowClick={onRowClick}
                actions={
                  typeof actions === "function" ? actions(data) : actions
                }
                getRowStyle={getRowStyle}
                getCellStyle={
                  getCellStyle
                    ? (col, rowItem) => getCellStyle(col, rowItem, index)
                    : undefined
                }
              />
            ))}
          </Table.Body>
        </Table>
      </OverflowContainer>
    </TableWrapper>
  );
};

export default React.memo(ConfigurableTable) as typeof ConfigurableTable;
